/* Miles motion — scroll choreography.

   The design lives in miles-motion.css; this script only decides WHEN. It
   tags reveal targets with data-mm and a per-item --mm-d stagger delay, then
   flips .mm-in as each cluster crosses the trigger line. Because the hidden
   state keys off an attribute only this script assigns, a visitor without
   JavaScript — or a failed script load — sees the site exactly as it renders
   today. The hero and 404 entrances are pure CSS and never pass through here.

   Two contracts worth keeping:

   - Only non-interactive wrappers are tagged. transition-delay applies to
     EVERY transition on the element, so a tagged link or button would lag its
     hover response by its stagger slot. CTAs, arrows and inputs ride inside a
     tagged parent instead.
   - The carousel owns its slides. miles-carousel.js writes inline opacity and
     transform on .miles-carousel__slide, so the reveal targets the stage as a
     single surface and never touches a slide. */

(function () {
  var root = document.documentElement;
  if (!root.classList.contains('miles-motion')) return;
  if (!('IntersectionObserver' in window)) return;

  /* Each entry is one reveal cluster: a root that triggers, and the items
     inside it that rise on a shared stagger. No `items` means the root
     reveals as a single surface. DOM order sets the stagger order. */
  var CLUSTERS = [
    {
      root:
        '.miles-section__header, .miles-guard__header, .miles-steps__header, ' +
        '.miles-carousel__header, .miles-trust__header, .miles-capture__copy',
      items: ':scope > *',
      stagger: 90
    },
    {
      root: '.miles-media-text',
      items: ':scope > .miles-media-text__media, :scope > .miles-media-text__copy > *',
      stagger: 90
    },
    { root: '.miles-band__lead' },
    { root: '.miles-band__grid', items: ':scope > .miles-band__card', stagger: 100 },
    { root: '.miles-guard__grid', items: ':scope > .miles-guard__block', stagger: 130 },
    { root: '.miles-trust__grid', items: ':scope > .miles-trust__card', stagger: 80 },
    { root: '.miles-plans__summary', items: ':scope > *', stagger: 90 },
    /* The three priced cards are the moment on this page, so they rise a beat
       apart rather than as one surface. Tagging the CARD and not its button
       keeps the contract above: transition-delay lands on the tagged element,
       and the add-to-cart inside it answers a hover at full speed. */
    { root: '.miles-plans__terms', items: ':scope > .miles-plans__card', stagger: 110 },
    { root: '.miles-membership__card', items: ':scope > *', stagger: 80 },
    {
      root: '.miles-membership__assurances',
      items: ':scope > .miles-membership__assurance',
      stagger: 70
    },
    { root: '.miles-steps__list', items: ':scope > .miles-steps__item', stagger: 150 },
    { root: '.miles-compat' },
    { root: '.miles-carousel__stage' },
    { root: '.miles-carousel__dots' },
    { root: '.miles-capture__form' }
  ];

  var groups = [];

  /* instant reveals skip the stagger and shorten the fade (the .mm-now
     class) — used when the choreography would otherwise be seen at the
     wrong moment: focus landing inside an unrevealed cluster, or a cluster
     the visitor teleported past. */
  function revealGroup(group, instant) {
    if (group.revealed) return;
    group.revealed = true;
    observer.unobserve(group.root);
    group.items.forEach(function (item) {
      if (instant) item.classList.add('mm-now');
      item.classList.add('mm-in');
    });
  }

  var observer = new IntersectionObserver(
    function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        groups.forEach(function (group) {
          if (group.root === entry.target) revealGroup(group, false);
        });
      });
    },
    /* Trigger when a cluster's top clears the bottom 10% of the viewport —
       just inside the fold, so the rise is seen rather than already done. */
    { rootMargin: '0px 0px -10% 0px', threshold: 0 }
  );

  CLUSTERS.forEach(function (def) {
    document.querySelectorAll(def.root).forEach(function (rootEl) {
      /* The hero and stub run their own CSS entrances; the header and footer
         stay still on purpose. */
      if (rootEl.closest('.miles-hero, .miles-stub, .miles-header, .miles-footer')) return;

      var items = def.items ? rootEl.querySelectorAll(def.items) : [rootEl];
      if (!items.length) return;

      var tagged = [];
      Array.prototype.forEach.call(items, function (el, i) {
        el.setAttribute('data-mm', '');
        el.style.setProperty('--mm-d', i * (def.stagger || 0) + 'ms');
        tagged.push(el);
      });

      groups.push({ root: rootEl, items: tagged, revealed: false });
      observer.observe(rootEl);
    });
  });

  /* Focus must never land on invisible content. Some cluster roots ARE
     interactive surfaces (the carousel's arrows and dots, the compat and
     capture forms), and opacity: 0 leaves them fully focusable with no
     focus ring to show for it. A keyboard user tabbing into an unrevealed
     cluster gets it revealed on the spot instead. */
  document.addEventListener('focusin', function (event) {
    groups.forEach(function (group) {
      if (!group.revealed && group.root.contains(event.target)) {
        revealGroup(group, true);
      }
    });
  });

  /* Anchor jumps teleport — the header CTA lands on the last section, and
     IntersectionObserver never sees the nine clusters in between cross the
     viewport, so they would flash in one by one on the scroll back up.
     Anything the visitor has already passed reveals instantly instead. Runs
     on load too, for a page restored or entered mid-scroll. */
  function revealPassed() {
    groups.forEach(function (group) {
      if (!group.revealed && group.root.getBoundingClientRect().bottom < 0) {
        revealGroup(group, true);
      }
    });
  }

  var sweepQueued = false;
  window.addEventListener(
    'scroll',
    function () {
      if (sweepQueued) return;
      sweepQueued = true;
      requestAnimationFrame(function () {
        sweepQueued = false;
        revealPassed();
      });
    },
    { passive: true }
  );
  window.addEventListener('hashchange', revealPassed);
  revealPassed();

  /* The Guard pulse runs only while the band is actually on screen. */
  var guardInsets = document.querySelectorAll('.miles-guard__inset');
  if (guardInsets.length) {
    var pulseObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          entry.target.classList.toggle('mm-pulse', entry.isIntersecting);
        });
      },
      { threshold: 0.25 }
    );
    Array.prototype.forEach.call(guardInsets, function (el) {
      pulseObserver.observe(el);
    });
  }
})();
